import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Typography, Wrapper } from '@screentone/core';
import debounce from 'lodash.debounce';

import { ResponsiveLoader } from 'components';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { Article } from 'components/datamodel/content/Article/Article';
import { NoSearchResults } from 'components/no-search-results/NoSearchResults';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { ArticleItem } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import {
  AllessehContent,
  AllessehContentQueryBody,
  AllessehQueryRule,
  useAllessehContentQuery
} from 'hooks/useAllessehContentQuery';
import { isNonEmptyQueryForAllessehQueryRules, mergeAllessehQueryBodies, validateValue } from 'utils/queryUtils';
import styles from './QueryRulesItems.module.scss';

import { QUERY_FETCH_COUNT } from 'components/datamodel/container/List/List.utils'

interface QueryRulesItemsProps {
  allessehJsonQuery: string | null;
  lastArticleItemIndex: number;
  setJsonQueryStr: (newQueryStr: string) => void;
  onNumItemsLoaded: (numItemsFromQuery: number) => void;
}
export const QueryRulesItems = ({
  allessehJsonQuery,
  lastArticleItemIndex,
  setJsonQueryStr,
  onNumItemsLoaded
}: QueryRulesItemsProps) => {
  const { fromAllessehContent } = useDataModelContext();
  const { renderActions } = useContextMenuActions();
  const [debouncedQueryBody, setDebouncedQueryBody] = useState<AllessehContentQueryBody>({});
  const [hasQuery, setHasQuery] = useState(false);
  const { data: publicationSettingsResp } = usePublicationSettings();

  const debouncedUpdate = debounce((allessehJsonQuery?: string | null) => {
    const parsedAllessehQuery = allessehJsonQuery ? (JSON.parse(allessehJsonQuery) as AllessehContentQueryBody) : null;
    const fullAllessehQuery = mergeAllessehQueryBodies(
      JSON.stringify({ count: QUERY_FETCH_COUNT }),
      publicationSettingsResp?.publicationSetting.baseAllessehQuery,
      allessehJsonQuery
    );
    setDebouncedQueryBody(fullAllessehQuery);
    setHasQuery(isNonEmptyQueryForAllessehQueryRules(parsedAllessehQuery));
  }, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateDebouncedQueryBody = useCallback(debouncedUpdate, [publicationSettingsResp]);

  useEffect(() => {
    updateDebouncedQueryBody(allessehJsonQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allessehJsonQuery]);

  const {
    data,
    isLoading: isLoad,
    fetchStatus
  } = useAllessehContentQuery(debouncedQueryBody, {
    enabled: hasQuery,
    publicationKey: publicationSettingsResp?.publicationSetting.publicationKey
  });
  const isLoading = isLoad && fetchStatus !== 'idle';
  const contents = useMemo(
    () =>
      data
        ? (data.pages
            .map((page) => page.data?.attributes)
            .flat()
            .filter((x) => !!x) as AllessehContent[])
        : [],
    [data]
  );

  const { currentProperty } = useAuth();

  // NOTE: This component refetches all query items whenever an article or linked item is added to the collection.
  // This causes a brief moment where contents is empty but allessehJsonQuery exists (during refetch).
  // There are 3 data states:
  // 1. No allessehJsonQuery at all (before adding any rule, or after "clear query" is clicked)
  // 2. Query exists BUT content hasn't loaded yet (this causes UI flashing during refetch)
  // 3. Query exists AND content has loaded
  // Ideally we'd fix the constant refetching, but as a simpler workaround, this logic only
  // calls onNumItemsLoaded when: (a) no query exists and no content, or (b) query exists with content.
  // This avoids calling onNumItemsLoaded during the intermediate empty state during refetch.
  useEffect(() => {
    if (contents.length === 0 && !allessehJsonQuery) {
      onNumItemsLoaded(contents.length);
    } else if (hasQuery && contents.length > 0) {
      onNumItemsLoaded(contents.length);
    }
  }, [contents, onNumItemsLoaded, hasQuery, allessehJsonQuery]);

  const parsedAllessehQuery = allessehJsonQuery ? (JSON.parse(allessehJsonQuery) as AllessehContentQueryBody) : null;

  const noResultsMessage = validateValue(
    currentProperty,
    parsedAllessehQuery?.query as { [key: string]: AllessehQueryRule[] },
    publicationSettingsResp?.publicationSetting.queryRuleSchema
  );

  if (!hasQuery) {
    return (
      <Wrapper margin={{ bottom: 'sm' }} data-testid="collection-no-query-rule">
        <Typography variant="note">No query rules set</Typography>
      </Wrapper>
    );
  }

  if (isLoading) {
    return (
      <Wrapper padding={{ bottom: 'sm' }}>
        <ResponsiveLoader />
      </Wrapper>
    );
  }

  if (contents.length === 0) return <NoSearchResults title="Results" bodyText={noResultsMessage} />;

  return (
    <Wrapper className={styles.contentList} data-testid="query-content-list">
      {contents.map((content, index) => {
        const article = fromAllessehContent(content) as ArticleItem;
        return (
          <Article
            key={article.attributes.id}
            data={article}
            index={index}
            isDraft
            renderActions={() =>
              renderActions(MenuActionsKeys.CollectionDraftQueryArticle, {
                entity: article,
                hierarchyId: `0-${lastArticleItemIndex + 1}`,
                index,
                extraProperties: {
                  allessehContent: content,
                  jsonQueryStr: allessehJsonQuery ?? undefined,
                  setJsonQueryStr
                }
              })
            }
          />
        );
      })}
    </Wrapper>
  );
};
